import storage from "./storage";

// The Historical date range the person chose (bottom-left of the dashboard: Today, Last 7 Days, ..., Custom Date). It used to live only in a module
// variable, so every refresh put it back to Today. Saved on every change and restored when the dashboard loads.
//
// A preset is saved by NAME only and re-resolved against the live list on restore: "Last 7 Days" must mean the last seven days as of TODAY, not as of the
// day it was chosen. A custom range saves its start; its end is always "now" (the Sidebar says so), so it is not saved.
export const HISTORICAL_RANGE_KEY = "historicalRange";

export interface SavedRange {
  name: string;
  start?: number; // custom range only, ms since epoch
}

export interface RangeEntry {
  name: string;
  value: any;
}

/** Is this a date we can build an ISO string from? (the storage layer hands back "" for a key that is not set, and new Date("").toISOString() THROWS) */
export const isUsableTime = (value: unknown): boolean => {
  if (value === undefined || value === null || value === "") {
    return false;
  }

  return Number.isFinite(new Date(value as any).getTime());
};

/** The value if it is a usable date, otherwise the fallback: nothing downstream is ever given an empty date. */
export const usableTimeOr = (value: unknown, fallback: number): number => (isUsableTime(value) ? new Date(value as any).getTime() : fallback);

export const saveHistoricalRange = (range: { name?: string; value?: any } | null | undefined): void => {
  try {
    if (!range?.name) {
      return;
    }
    const saved: SavedRange = { name: range.name };
    if (range.name === "Custom Date") {
      const start = new Date(range.value).getTime();
      if (!Number.isFinite(start)) {
        return;
      }
      saved.start = start;
    }
    storage.set(HISTORICAL_RANGE_KEY, JSON.stringify(saved));
  } catch (e) {
    // a failed save only means the choice does not survive a refresh
  }
};

export const clearHistoricalRange = (): void => {
  try {
    storage.remove(HISTORICAL_RANGE_KEY);
  } catch (e) {
    // nothing to clear
  }
};

// What to select on load, or null to leave the default. `list` is the presets available to THIS account (a plan can restrict some), so a saved range the
// account may no longer use falls back to the default instead of resurrecting a locked choice.
export const resolveSavedRange = (
  saved: SavedRange | null | undefined,
  list: RangeEntry[] | undefined,
  nowMs: number = Date.now()
): RangeEntry | null => {
  if (!saved || typeof saved.name !== "string" || !list) {
    return null;
  }
  if (saved.name === "Custom Date") {
    const start = Number(saved.start);
    if (!Number.isFinite(start) || start > nowMs) {
      return null;
    }

    return { name: "Custom Date", value: start };
  }

  // A preset is only restored if its date is USABLE now. "All" starts at the account's creation date, read from storage, and a missing key comes back as "" (not undefined):
  // restoring it gave a range with an empty start, and the dashboard crashed on a fresh load (staging, 2026-10-17).
  return list.find((entry) => entry.name === saved.name && entry.name !== "Custom Date" && isUsableTime(entry.value)) ?? null;
};

export const readSavedRange = (): SavedRange | null => {
  try {
    const parsed = storage.getJSON<SavedRange>(HISTORICAL_RANGE_KEY);

    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (e) {
    return null;
  }
};
